import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import ManagePage from "~components/ManagePage/ManagePage";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type { EditFixtureForm } from "~pages/Fixtures/types";
import {
  FixtureAdvancedSection,
  FixtureBasicSection,
  FixtureCompetitorsSection,
  FixtureEntrantsSkeleton,
  FixtureParticipantsSection,
} from "./components";
import { StyledFormGroupsContainer } from "./FixtureManagePage.styles";
import { useEditFixtureFormSubmit, useFixtureName } from "./hooks";
import { StyledEntrantSection } from "./styles";

const FixtureManagePage = () => {
  const { data: fixtureData, isLoading } = useGetFixture();
  const [isUpdating, setIsUpdating] = useState(false);
  const onFinish = useEditFixtureFormSubmit();
  const fixtureName = useFixtureName();
  const { hasPermission } = useUserPermissions();

  const isCompetitors = fixtureData?.competitorType === CompetitorTypes.Entity;
  const isParticipants = fixtureData?.competitorType === CompetitorTypes.Person;

  const readFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.READ);
  const readParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.READ);
  const readCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.READ);
  const nothingAllowed = !readFixtureAllowed && !readParticipantsAllowed && !readCompetitorsAllowed;

  const navigate = useNavigate();
  const { customerId, sport, organizationId, seasonId, fixtureId } = useParams();

  useEffect(() => {
    if (nothingAllowed) navigate(`/${customerId}/admin/${sport}/${organizationId}/403`);
  }, [nothingAllowed, customerId, sport, organizationId, navigate]);

  const goToFixtures = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`);

  const permissionNames = isParticipants
    ? [PermissionNames.FIXTURE, PermissionNames.FIXTURE_PERSON]
    : [PermissionNames.FIXTURE, PermissionNames.FIXTURE_ENTITY];

  const handleSubmit = async (values: EditFixtureForm) => {
    setIsUpdating(true);
    try {
      await onFinish(values);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <ManagePage<EditFixtureForm>
      key={fixtureId}
      isLoading={isLoading}
      isSaving={isUpdating}
      entityType="fixture"
      permissionNames={permissionNames}
      entityName={fixtureName}
      onBack={goToFixtures}
      onSubmit={handleSubmit}
      initialValues={fixtureData?.formData}
    >
      <StyledFormGroupsContainer>
        <FixtureBasicSection isUpdating={isUpdating} />
        {readFixtureAllowed && <FixtureAdvancedSection isUpdating={isUpdating} />}

        {isLoading && <FixtureEntrantsSkeleton />}
        <StyledEntrantSection>
          {isCompetitors && readCompetitorsAllowed && (
            <>
              <FixtureCompetitorsSection isUpdating={isUpdating} isHome />
              <FixtureCompetitorsSection isUpdating={isUpdating} />
            </>
          )}
          {isParticipants && readParticipantsAllowed && (
            <>
              <FixtureParticipantsSection isUpdating={isUpdating} isHome />
              <FixtureParticipantsSection isUpdating={isUpdating} />
            </>
          )}
        </StyledEntrantSection>
      </StyledFormGroupsContainer>
    </ManagePage>
  );
};

export default FixtureManagePage;
